import { ReactNode, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { createTableSyncActions, createTableAsyncActions, TableAction } from './actions';
import { TableContext } from './TableContext';
import { createInitialState, tableReducer } from './reducer';
import { createTableMiddleware } from './middlewares/createTableMiddleware';
import { TableApiConfig } from './types';

interface TableProviderProps<T> {
  children: ReactNode;
  apiConfig: TableApiConfig<T>;
  initialPageSize?: number;
  /** Filtres appliqués d'entrée et cible du reset (ex. { isTest: false }). */
  initialFilters?: Record<string, any>;
  autoFetchOnMount?: boolean;
  autoFetchFiltersOnMount?: boolean;
  refreshTrigger?: number;
}

const TableProvider = <T,>({
  children,
  apiConfig,
  initialPageSize = 10,
  initialFilters,
  autoFetchOnMount = true,
  autoFetchFiltersOnMount = true,
  refreshTrigger
}: TableProviderProps<T>) => {
  // Signature stable pour ne pas recréer l'état à chaque rendu (objet inline).
  const initialFiltersSignature = JSON.stringify(initialFilters ?? {});
  const initialState = useMemo(() => ({
    ...createInitialState<T>(initialFilters),
    pageSize: initialPageSize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [initialPageSize, initialFiltersSignature]);

  const [state, baseDispatch] = useReducer(tableReducer<T>, initialState);

  const stateRef = useRef(state);
  stateRef.current = state;
  const getState = useCallback(() => stateRef.current, []);

  const middlewareRef = useRef<any>(null);

  const dispatch = useCallback(
    (action: TableAction) => {
      if (middlewareRef.current) {
        middlewareRef.current(baseDispatch)(action);
      } else {
        baseDispatch(action);
      }
    },
    []
  );

  const syncActions = useMemo(
    () => createTableSyncActions(dispatch),
    [dispatch]
  );

  const asyncActions = useMemo(
    () => createTableAsyncActions(apiConfig, syncActions, getState),
    [apiConfig, syncActions, getState]
  );

  const middleware = useMemo(
    () => createTableMiddleware(asyncActions, getState),
    [asyncActions, getState]
  );

  middlewareRef.current = middleware;

  const value = useMemo(() => ({
    state,
    dispatch,
    ...syncActions,
    ...asyncActions
  }), [state, dispatch, syncActions, asyncActions]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetchOnMount) {
      asyncActions.fetchDataAsync();
    }
    if (autoFetchFiltersOnMount && apiConfig.fetchFilters) {
      asyncActions.fetchFiltersAsync();
    }
  }, []);

  // Refresh when trigger changes (skip initial mount)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    asyncActions.fetchDataAsync();
  }, [refreshTrigger]);

  return (
    <TableContext.Provider value={value}>
      {children}
    </TableContext.Provider>
  );
};

export default TableProvider;
